"use client"

import {
  Loader2,
  Send,
  Wand2,
  LayoutGrid,
  FileText,
  Search,
  Truck,
  Users,
  PenTool,
  Send as SendIcon,
  ShieldCheck,
  BarChart3,
  Mic,
  Headphones,
  Radar,
  TrendingUp,
  MessageCircle,
  Code2,
  Megaphone,
  Scale,
  FolderArchive,
  ClipboardList,
  CandlestickChart,
  ArrowLeftRight,
  BookOpen,
  Sparkles,
  ScrollText,
} from "lucide-react"
import type { AgentDemoStep } from "@/lib/agent-demo-data"
import { AgentSidebar } from "./agent-sidebar"
import { SafeClawLogo } from "@/src/components/ui/safeclaw-logo"
import { DEMO_SCENARIO_LIST } from "@/lib/demo-scenarios"
import { ConversationPublishPanel } from "./conversation-publish-modal"
import { GlassToast } from "@/src/components/ui/glass-toast"
import { AgentMessageList } from "./agent-message-list"
import { useAgentPlayback } from "@/lib/hooks/use-agent-playback"

const ROLE_ICONS: Record<string, React.ReactNode> = {
  // ── Original ──
  "环境": <LayoutGrid className="h-3.5 w-3.5" />,
  "后勤": <Truck className="h-3.5 w-3.5" />,
  "整合": <Wand2 className="h-3.5 w-3.5" />,
  // ── Core ──
  "数据": <BarChart3 className="h-3.5 w-3.5" />,
  "研究": <Search className="h-3.5 w-3.5" />,
  "调研": <Search className="h-3.5 w-3.5" />,
  "分析": <BarChart3 className="h-3.5 w-3.5" />,
  "协作": <Users className="h-3.5 w-3.5" />,
  "设计": <PenTool className="h-3.5 w-3.5" />,
  "创作": <Sparkles className="h-3.5 w-3.5" />,
  "交付": <SendIcon className="h-3.5 w-3.5" />,
  // ── Media & Content ──
  "音频": <Headphones className="h-3.5 w-3.5" />,
  "转录": <Mic className="h-3.5 w-3.5" />,
  "采集": <Radar className="h-3.5 w-3.5" />,
  "舆情": <Megaphone className="h-3.5 w-3.5" />,
  // ── E-Commerce ──
  "运营": <TrendingUp className="h-3.5 w-3.5" />,
  "增长": <TrendingUp className="h-3.5 w-3.5" />,
  "客服": <MessageCircle className="h-3.5 w-3.5" />,
  "服务": <MessageCircle className="h-3.5 w-3.5" />,
  // ── Legal & Compliance ──
  "法律": <Scale className="h-3.5 w-3.5" />,
  "法务": <Scale className="h-3.5 w-3.5" />,
  "合规": <ShieldCheck className="h-3.5 w-3.5" />,
  "风控": <ShieldCheck className="h-3.5 w-3.5" />,
  "安全": <ShieldCheck className="h-3.5 w-3.5" />,
  // ── Document & Project ──
  "文档": <FileText className="h-3.5 w-3.5" />,
  "解析": <ScrollText className="h-3.5 w-3.5" />,
  "整理": <FolderArchive className="h-3.5 w-3.5" />,
  "记录": <FolderArchive className="h-3.5 w-3.5" />,
  "项目管理": <ClipboardList className="h-3.5 w-3.5" />,
  "开发": <Code2 className="h-3.5 w-3.5" />,
  // ── Finance ──
  "量化": <CandlestickChart className="h-3.5 w-3.5" />,
  "策略": <BookOpen className="h-3.5 w-3.5" />,
  "交易": <ArrowLeftRight className="h-3.5 w-3.5" />,
}

/* ══════════════════════════════════════════════════ */
export function AgentDemo({ initialQuery = "", demoSteps, selectedScenarioId }: { initialQuery?: string; demoSteps: AgentDemoStep[]; selectedScenarioId?: string }) {
  const pb = useAgentPlayback(demoSteps, selectedScenarioId, initialQuery)

  return (
    <div className="flex flex-1 flex-col overflow-hidden relative">
      {/* Logo — 固定左上角，不随对话滚动 */}
      {pb.visibleSteps.length > 0 && (
        <div className="absolute top-4 left-4 z-10 animate-in fade-in duration-500">
          <SafeClawLogo
            height={26}
            className="opacity-50 hover:opacity-90 transition-opacity cursor-pointer"
          />
        </div>
      )}

      {/* ── Main ── */}
      <AgentMessageList
        visibleSteps={pb.visibleSteps}
        showInput={pb.showInput}
        mainToolsState={pb.mainToolsState}
        liveSubAgents={pb.liveSubAgents}
        allSubAgentsDone={pb.allSubAgentsDone}
        selectedAgentId={pb.selectedAgentId}
        onAgentClick={(agentId) => {
          pb.userPickedRef.current = true
          const nextId = pb.selectedAgentId === agentId ? null : agentId
          pb.setSelectedAgentId(nextId)
          if (nextId) pb.setShowPublishModal(false)
        }}
        roleIcons={ROLE_ICONS}
        scenarioSkills={pb.scenarioSkills}
        onSkillsLoaded={() => pb.setSkillsLoaded(true)}
        onPublish={() => { pb.setShowPublishModal(true); pb.setSelectedAgentId(null) }}
        scrollRef={pb.scrollRef}
        onScroll={pb.handleScroll}
      />

      {/* ── Floating Input Pill ── */}
      <div
        className="pointer-events-none absolute left-0 right-0 bottom-0 z-30 flex justify-center pb-4 px-4"
        style={{
          paddingRight: (pb.selectedAgent || pb.showPublishModal) ? "calc(28rem + 1rem + 1rem)" : undefined,
          transition: "padding-right 300ms cubic-bezier(0.16, 1, 0.3, 1)",
        }}
      >
        <div
          className="pointer-events-auto w-full max-w-2xl liquid-glass-float !rounded-full flex items-center gap-2 pl-7 pr-2 py-2"
        >
          <div className="flex-1 relative min-w-0 flex items-center">
            <input
              type="text"
              readOnly
              value={pb.showInput ? initialQuery || "" : ""}
              placeholder={pb.isRunning ? "SafeClaw 正在执行任务..." : "输入您的需求..."}
              className="w-full bg-transparent text-sm text-foreground placeholder:text-muted-foreground outline-none caret-transparent"
            />
            {/* Custom breathing caret */}
            {!pb.showInput && !pb.isRunning && (
              <span className="absolute left-0 top-1/2 -translate-y-1/2 w-[2px] h-[18px] rounded-full bg-primary/80 caret-breathe" />
            )}
          </div>
          <button
            onClick={pb.showInput ? pb.startDemo : undefined}
            disabled={pb.isRunning || !pb.showInput}
            className="flex h-9 w-9 items-center justify-center rounded-full bg-primary text-primary-foreground hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed shrink-0"
          >
            {pb.isRunning ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Send className="h-4 w-4" />
            )}
          </button>
        </div>
      </div>

      {/* ── Sidebar ── */}
      <AgentSidebar
        agent={pb.selectedAgent}
        startTimes={pb.agentStartTimes}
        onClose={() => { pb.userPickedRef.current = true; pb.setSelectedAgentId(null) }}
        richCards={demoSteps.find(s => s.type === "summary")?.richCards}
      />

      {/* ── Publish to community side panel ── */}
      {(() => {
        const scenario = DEMO_SCENARIO_LIST.find((s) => s.id === selectedScenarioId)
        return (
          <ConversationPublishPanel
            open={pb.showPublishModal}
            onOpenChange={pb.setShowPublishModal}
            scenarioId={selectedScenarioId}
            scenarioTitle={scenario?.title ?? ""}
            scenarioDescription={scenario?.description ?? ""}
            initialQuery={initialQuery}
            demoSteps={demoSteps}
          />
        )
      })()}

      {/* ── Share notification toast ── */}
      <GlassToast
        open={pb.showShareToast && !pb.showPublishModal}
        onOpenChange={pb.setShowShareToast}
        duration={12000}
        position="top-center"
      >
        <div className="flex items-center gap-3">
          <div
            className="flex items-center justify-center h-8 w-8 rounded-xl shrink-0"
            style={{
              background: "linear-gradient(135deg, oklch(0.50 0.24 320), oklch(0.55 0.20 15))",
            }}
          >
            <Sparkles className="h-3.5 w-3.5 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-xs font-semibold text-foreground">任务已完成</p>
            <p className="text-caption text-muted-foreground mt-0.5">
              分享这套工作流，让更多人受益
            </p>
          </div>
          <button
            type="button"
            onClick={() => {
              pb.setShowShareToast(false)
              pb.setShowPublishModal(true)
              pb.setSelectedAgentId(null)
            }}
            className="px-3 py-1.5 text-caption font-semibold text-white rounded-lg shrink-0 transition-opacity hover:opacity-90"
            style={{
              background: "linear-gradient(135deg, oklch(0.50 0.24 320), oklch(0.55 0.20 15))",
            }}
          >
            去分享
          </button>
        </div>
      </GlassToast>
    </div>
  )
}
